import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import { useNavigate, useParams } from "react-router-dom";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import dayjs from "dayjs";
import { getAllUserRequest } from "../../../../../redux/users/slice";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";

const StatusRenderer = ({ rowData }: any) => {
  return (
    <Box sx={{ height: 52, display: "flex", alignItems: "center" }}>
      {rowData?.isActive ? (
        <Chip
          color="success"
          icon={<CheckOutlinedIcon />}
          label="Active"
          variant="outlined"
        />
      ) : (
        <Chip
          color="error"
          icon={<CloseOutlinedIcon />}
          label="Inactive"
          variant="outlined"
        />
      )}
    </Box>
  );
};

const NameRenderer = ({ rowData }: any) => {
  const navigate = useNavigate();
  const onViewDetailsClick = () => {
    const userId = rowData?.id;

    if (userId) {
      navigate(`/my-organization/users/${userId}`, {
        state: {
          id: userId,
          breadcrumb: {
            names: {
              [userId]: `${rowData?.firstName} ${rowData?.lastName}`,
            },
          },
        },
      });
    }
  };
  return (
    <Box>
      <Button
        sx={{ textTransform: "none", px: 0 }}
        onClick={onViewDetailsClick}
      >
        <Typography>
          {rowData?.firstName} {rowData?.lastName}
        </Typography>
      </Button>
    </Box>
  );
};

export default function Users() {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowData, setRowData] = useState<any>([]);
  const [filterParams, setFilterParams] = useState({});

  const dispatch = useDispatch();
  const { inspectionFirmId } = useParams();

  const { loading } = useSelector((state: any) => state.user);
  const { allUsers } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(allUsers?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allUsers?.totalCount !== undefined) {
      rowCountRef.current = allUsers?.totalCount;
    }
    return rowCountRef.current;
  }, [allUsers?.totalCount]);

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  }, []);

  useEffect(() => {
    setRowData(allUsers?.items);
  }, [allUsers]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const column: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      headerClassName: "hideRightSeparator",
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer
          rowData={params.row}
          // firmData={data}
          inspectionFirmId={inspectionFirmId}
        />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="username"
          sortField="username"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryEmail",
      headerName: "Primary Email",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryEmail"
          sortField="email"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryPhone",
      headerName: "Primary Phone",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryPhone"
          sortField="phone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <StatusRenderer rowData={params?.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isActive"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          values={["Active", "Inactive"]}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "roleNames",
      headerName: "Role(s)",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="roleName"
          sortField="role"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "lastLoginDateTime",
      headerName: "Last Login",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="lastLogin"
          sortField="lastLogin"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          onFilterSubmit={onFilterUsers}
          showFilter={showTableFilter}
        />
      ),
    },
  ];

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllUserRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  return (
    <>
      <Box
        sx={{
          m: 2,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <IconButton
          aria-label="filter"
          onClick={() => onFilterClick(!showTableFilter)}
        >
          {showTableFilter ? (
            <FilterAltOffOutlinedIcon color="primary" />
          ) : (
            <FilterAltOutlinedIcon color="primary" />
          )}
        </IconButton>
      </Box>
      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          columnDef={column}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableColumnMenu
          headerHeight={headerHeight}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={loading}
        />
      </Box>
    </>
  );
}
